import React, { useContext, FC, useCallback, Fragment, forwardRef, RefAttributes, RefObject } from 'react';
import { FocusOn } from 'react-focus-on';
import { NavContext } from './NavContext';
import { isUndefined, indexOf, isEmpty, map } from 'lodash';
import {
  NavMenuVariant,
  ElementProps,
  AnchorElementProps,
  ButtonElementProps,
  NavMenuElementProps,
  NavElementProps,
  NavContextProps
} from './SideNavTypes';
import { setRelAttribute } from '@trip-ux/common';
import { NAV_BAR_USER_ITEMS, AUTH_HEADERS, MANULIFE_NAV_ITEMS, AGENT_ADMINISTRATION_PERMISSIONS } from '@trip-ux/constants';
import { getManulifeID } from '@trip-ux/common';
import { useAdAuth } from '@trip-ux/components';
import { useSelector } from 'react-redux';
import { ReduxStore } from '@trip-ux/redux-store';

export const Element: FC<ElementProps> = ({ src, label, showDrop, dropExpanded = false, isMobile = false }) => (
  <>
    {src && <img src={src} alt={label} aria-hidden="true" />}
    <span className="menu-label" aria-hidden="true">
      {label}
    </span>
    {showDrop && <span className={`${dropExpanded ? 'drop-open' : 'drop-close'} ${isMobile ? 'mobile' : ''}`} />}
  </>
);

export const AnchorElement: FC<AnchorElementProps & RefAttributes<HTMLAnchorElement>> = forwardRef<
  HTMLAnchorElement,
  AnchorElementProps
>(({ href, className, onClick, navId, ariaLabel, label, isMobile = false, logo }, ref) => (
  <a
    href={href}
    className={className}
    onClick={onClick}
    id={navId}
    data-testid={navId}
    aria-label={ariaLabel ? ariaLabel : label}
    ref={ref}
    rel={setRelAttribute(href)}>
    <Element src={logo} label={label} isMobile={isMobile} />
  </a>
));
AnchorElement.displayName = 'AnchorElement';
export const ButtonElement: FC<ButtonElementProps & RefAttributes<HTMLButtonElement>> = forwardRef<
  HTMLButtonElement,
  ButtonElementProps
>(({ className, onClick, navId, ariaLabel, label, isMobile, logo, dropOpen, showDrop }, ref) => (
  <button
    onClick={onClick}
    className={className}
    id={navId}
    data-testid={navId}
    aria-haspopup={isUndefined(dropOpen) ? dropOpen : undefined}
    aria-expanded={dropOpen}
    aria-label={ariaLabel ? ariaLabel : label}
    ref={ref}>
    <Element src={logo} label={label} showDrop={showDrop} dropExpanded={dropOpen} isMobile={isMobile} />
  </button>
));
ButtonElement.displayName = 'ButtonElement';
export const NavMenuElement: FC<NavMenuElementProps & RefAttributes<HTMLButtonElement | HTMLAnchorElement>> =
  forwardRef<HTMLButtonElement | HTMLAnchorElement, NavMenuElementProps>(
    (
      { href, className, onClick, navId, ariaLabel, label, isMobile = false, invertedLogo, logo, dropOpen, showDrop },
      ref
    ) =>
      href ? (
        <AnchorElement
          href={href}
          className={className}
          onClick={onClick}
          navId={navId}
          ariaLabel={ariaLabel}
          label={label}
          isMobile={isMobile}
          logo={isMobile ? invertedLogo : logo}
          ref={ref as RefObject<HTMLAnchorElement>}
        />
      ) : (
        <ButtonElement
          onClick={onClick}
          className={className}
          navId={navId}
          dropOpen={dropOpen}
          ariaLabel={ariaLabel}
          label={label}
          showDrop={showDrop}
          logo={isMobile ? invertedLogo : logo}
          isMobile={isMobile}
          ref={ref as RefObject<HTMLButtonElement>}
        />
      )
  );
NavMenuElement.displayName = 'NavMenuElement';
const isActive = (context: NavContextProps, eleId: string) =>
  indexOf(context.activeParents, eleId) !== -1 || context.activeKey === eleId;

const NavElementDropFocusOn = ({ isDropOpen, subMenuList }) => {
  const context = useContext(NavContext);
  const closeDrop = useCallback(() => context.setDropOpen(''), [context]);
  return (
    <FocusOn
      enabled={!context.isMobile && isDropOpen}
      autoFocus={true}
      returnFocus={true}
      onClickOutside={closeDrop}
      onEscapeKey={closeDrop}>
      <NavElementDrop subMenuList={subMenuList} isStatic={false} />
    </FocusOn>
  );
};

export const NavElementDrop = ({ subMenuList, isStatic }) => {
  const context = useContext(NavContext);

  const { lang, config, userProfile } = useSelector((store: ReduxStore) => ({
    lang: store.config.locale,
    userProfile: store.userProfile,
    config: store.config
  }));

  const adAuthHook = useAdAuth();
  const url = getManulifeID(config.options.manulifeId, lang);
  if (userProfile.userType === AUTH_HEADERS.LAN_ID) {
    subMenuList = subMenuList.filter(menu => MANULIFE_NAV_ITEMS.includes(menu.navId));
  }
  if (!userProfile.claims.includes(AGENT_ADMINISTRATION_PERMISSIONS.AGENT_ORGANIZATION_READ)) {
    subMenuList = subMenuList.filter(items => items.navId !== NAV_BAR_USER_ITEMS.AGENCY)
  }
  return (
    <div className={`sub-menu-drop ${isStatic && 'position-static h-100'}`}>
      <ul>
        {map(subMenuList, item => {
          const activeEle = isActive(context, item.navId);
          const hasSubMenu = !isEmpty(item.subMenuList);
          const dropOpen = context.dropSubMenuOpen === item.navId;
          return (
            <Fragment key={`${item.navId}-drop`}>
              <li>
                <NavMenuElement
                  href={item.href}
                  navId={item.navId}
                  ariaLabel={item.ariaLabel}
                  label={item.label}
                  invertedLogo={item.invertedLogo}
                  logo={item.logo}
                  className={`nav-drop-element ${activeEle ? 'active' : ''} ${NavMenuVariant.HORIZONTAL}`}
                  showDrop={hasSubMenu}
                  dropOpen={dropOpen}
                  onClick={e => {
                    if (!hasSubMenu) {
                      context.setDropOpen('');
                      context.setMobileToggle(false);
                      context.setActiveKey(item.navId);
                      switch (item.navId) {
                        case NAV_BAR_USER_ITEMS.USER_LOGOUT:
                          e.preventDefault();
                          adAuthHook?.logout();
                          break;
                        case NAV_BAR_USER_ITEMS.MANULIFEID:
                          e.preventDefault();
                          window.location.href = url;
                          break;
                        case NAV_BAR_USER_ITEMS.PREFERENCES:
                          e.preventDefault();
                          window.location.href = config.options[`preferences${lang.toUpperCase()}`];
                          break;

                        default:
                          break;
                      }
                    } else {
                      context.setDropSubMenuOpen(item.navId);
                    }
                    context.onSelect(item.navId, e);
                  }}
                />
              </li>
              {dropOpen && (
                <>
                  {item.subMenuList.map(subItem => (
                    <li key={`${subItem.navId}-drop-sub`}>
                      <NavMenuElement
                        href={subItem.href}
                        navId={subItem.navId}
                        ariaLabel={subItem.ariaLabel}
                        label={subItem.label}
                        invertedLogo={subItem.invertedLogo}
                        logo={subItem.logo}
                        className={`nav-drop-sub-element ${context.activeKey === subItem.navId ? 'active' : ''} ${NavMenuVariant.HORIZONTAL
                          } `}
                        onClick={e => {
                          context.setDropOpen('');
                          context.setMobileToggle(false);
                          context.setActiveKey(subItem.navId);
                          context.onSelect(subItem.navId, e);
                          context.setDropSubMenuOpen('');
                        }}
                      />
                    </li>
                  ))}
                </>
              )
              }
            </Fragment>
          );
        })}
      </ul>
    </div >
  );
};

export const NavElement: FC<NavElementProps> = ({
  href,
  variant,
  className = '',
  navId,
  subMenuList,
  ariaLabel,
  label,
  logo,
  invertedLogo,
  disableDrop = false
}) => {
  const context = useContext(NavContext);
  const alignment = variant ? variant : context.isMobile ? NavMenuVariant.HORIZONTAL : NavMenuVariant.VERTICAL;
  const onClick = useCallback(
    e => {
      if (isEmpty(subMenuList)) {
        context.setDropOpen('');
        context.setActiveKey(navId);
        context.setMobileToggle(false);
      } else if (!isEmpty(subMenuList) && isEmpty(href)) {
        context.setDropOpen(navId);
      }
      context.onSelect(navId, e);
    },
    [subMenuList, context, navId, href]
  );
  const computedClassName = `${alignment} ${className} ${isActive(context, navId) ? 'active' : ''} ${context.isMobile ? 'mobile' : ''
    }`;
  const isDropOpen = !disableDrop && context.dropOpen === navId;
  return (
    <>
      <NavMenuElement
        href={href}
        className={computedClassName}
        onClick={onClick}
        navId={navId}
        ariaLabel={ariaLabel}
        label={label}
        isMobile={context.isMobile}
        invertedLogo={invertedLogo}
        logo={logo}
        dropOpen={isDropOpen}
        showDrop={!isEmpty(context.getParent(navId)) && !isEmpty(subMenuList)}
      />
      {isDropOpen && <NavElementDropFocusOn isDropOpen={isDropOpen} subMenuList={subMenuList} />}
    </>
  );
};

NavElement.displayName = 'NavElement';
export default NavElement;
